/**
 * The "what you'd unlock" preview, shown after /coupons/preview returns
 * but before the user has claimed. The Claim button is the entry point
 * into either the auth gate (if logged out) or the /coupons/claim
 * call (if logged in).
 */
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import { CouponPreview } from '../types';
import RewardSummary from './RewardSummary';

interface GiftPreviewCardProps {
  preview: CouponPreview;
  /** Whether the user is logged in — used to soften the CTA copy. */
  isAuthenticated: boolean;
  /** Async — caller drives claim. */
  onClaim: () => void;
  /** When true, disable + spinner on the Claim button. */
  claiming?: boolean;
}

const GiftPreviewCard: React.FC<GiftPreviewCardProps> = ({
  preview,
  isAuthenticated,
  onClaim,
  claiming = false,
}) => {
  // Render the expiry as a friendly date, falling back to the raw
  // string if Date parsing fails (which it shouldn't — the backend
  // always sends ISO 8601).
  const expiryLabel = (() => {
    const d = new Date(preview.expiresAt);
    if (Number.isNaN(d.getTime())) return preview.expiresAt;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  })();

  return (
    <Paper
      elevation={3}
      data-testid="gift-preview-card"
      sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <RedeemIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="overline" color="text.secondary">
            You won
          </Typography>
          <Typography
            variant="h5"
            component="h1"
            data-testid="gift-preview-tier"
          >
            {preview.tierName}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 1 }}>
        <RewardSummary reward={preview.reward} />
      </Box>

      <Typography variant="body2" color="text.secondary">
        Code: <code>{preview.code}</code> · Expires {expiryLabel}
      </Typography>

      <Button
        variant="contained"
        size="large"
        onClick={onClaim}
        disabled={claiming}
        data-testid="gift-preview-claim"
        sx={{ mt: 1 }}
      >
        {claiming ? (
          <CircularProgress size={24} />
        ) : isAuthenticated ? (
          'Claim now'
        ) : (
          'Sign in to claim'
        )}
      </Button>
    </Paper>
  );
};

export default GiftPreviewCard;
